import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, onUnmounted, ref } from 'vue';

import CountdownTimer from '@/features/sponsored-recommendations/components/CountdownTimer.vue';
import PictureComp from '@/components/picture/PictureComp.vue';

import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';

import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import { useCampaignInfo } from '@/features/sponsored-recommendations/composables/useSendBidId';

import { SraEventAction } from '@/@types/graphql-types';
import { ImpressionTrackingEvents } from '@/enums/events';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import type { Maybe } from '@/helpers/types';
import { PosterSponsoredRecImages } from '@/interfaces/components/poster/PosterGraphql';
import { createReusableTemplate } from '@vueuse/core';

/** Viewing thresholds in percentages at which we want to track. */
type TrackingWatchedPointsInPercentage = (typeof TrackingWatchedPointsInPercentage)[number];
type TrackingWatchedPointsInSeconds = (typeof TrackingWatchedPointsInSeconds)[number];
type WebKitHTMLVideoElement =
	| HTMLVideoElement
	// To support mobile safari browsers
	| (HTMLVideoElement & { webkitSupportsFullscreen: true; webkitEnterFullscreen: () => void });


export default /*#__PURE__*/_defineComponent({
  __name: 'SponsoredRecommendationPremiumVideo',
  props: {
    sponsoredAd: null,
    lazy: { type: Boolean, default: true },
    additionalContexts: { default: () => [] }
  },
  emits: ["onPosterClickOut", "imageError", "videoError"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'onPosterClickOut'): void;
	(e: 'imageError', src: Maybe<string>): void;
	(e: 'videoError', src: Maybe<string>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { sponsoredAd: NonNullable<SponsoredAdFragment>, lazy: boolean, additionalContexts: SnowplowContext[] };

const TrackingWatchedPointsInPercentage = [0, 25, 50, 75, 90, 100] as const;

/** Viewing thresholds in seconds at which we want to track. */
const TrackingWatchedPointsInSeconds = [2, 6, 15] as const;





const { campaignNode, promotionalTitle, promotionalText } = useCampaignInfo(() => props.sponsoredAd.campaign);

const { trackSraEvent } = useBackendTracking();

const disclaimerText = computed(() => props.sponsoredAd.campaign?.disclaimerText);

const isVideoAsGifCampaign = computed(() => props.sponsoredAd.campaign?.playSRVideoAsGif);

const getReleaseDate = computed<Date | undefined>(() => {
	if (!process.client || !props.sponsoredAd.campaign?.countdownTimer) return undefined;

	return new Date(props.sponsoredAd.campaign?.countdownTimer);
});

// Title Poster (to support AVIF)
const posterSRImage = computed<PosterSponsoredRecImages | null>(() => {
	let posterUrl = props.sponsoredAd.campaign?.posterOverride || campaignNode?.value.content.posterUrl;
	if (!posterUrl) return null;

	if (posterUrl.startsWith('/')) {
		posterUrl = `${JW_CONFIG.IMAGESCALER_URL}${posterUrl.replace('{profile}', 's332')}`;
	}

	return {
		imageUrl: posterUrl,
		sourceSets: [
			{
				srcSet: posterUrl.replace(/\.{?\w+}?$/, '.avif'),
				imageProp: {
					extension: '.avif',
					type: 'image/avif',
				},
				sourceProp: {
					viewport: 'desktop',
				},
				media: null,
			},
		],
	};
});

// Manage check to display countdown
onMounted(() => checkIfHasTimeRemaining());
onUnmounted(() => clearInterval(timerToMatchStarts));
const timerToMatchStarts = setInterval(checkIfHasTimeRemaining, 1000);

const hasTimeRemaining = ref(true);

function onVolumeChange(event: Event) {
	const change = (event.target as HTMLVideoElement).muted ? 'muted' : 'unmuted';
	isMuted.value = change === 'muted';

	if (!campaignNode.value) {
		return;
	}

	TrackingHelper.trackEvent(
		ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
		{
			action: 'toggle_mute',
			value: isMuted.value ? 1 : 0,
		},
		[titleContext.value]
	);
}

function checkIfHasTimeRemaining() {
	// If there is no release time, hide countdown
	if (!process.client || !getReleaseDate.value) {
		hasTimeRemaining.value = false;
		return;
	}

	const currentTime = new Date();

	// If release time is after the currentTime
	if (currentTime.getTime() >= getReleaseDate.value.getTime()) {
		hasTimeRemaining.value = false;
		return;
	}

	hasTimeRemaining.value = true;
}

// Manage Video

const videoElement = ref<WebKitHTMLVideoElement | null>(null);
const isMuted = ref(videoElement.value?.muted ?? true);

const { define: DefineVideoControls, reuse: ReuseVideoControls } = createReusableTemplate();

const volumeIcon = computed(() => {
	const filename = isMuted.value ? 'volume-xmark' : 'volume-high';
	return `/${ASSETS_DIR}/img/sponsored-recommendation-assets/${filename}.svg`;
});

const titleContext = computed(() => {
	return new SnowplowTitleContextGraphql(
		campaignNode.value?.objectId,
		campaignNode.value?.objectType,
		campaignNode.value?.__typename === 'Season' ? campaignNode.value?.content.seasonNumber : null
	);
});

function toggleMute() {
	isMuted.value = !isMuted.value;

	if (videoElement.value) {
		videoElement.value.muted = isMuted.value;
	}
}

/* Track Duration Play */
const videoDuration = ref(0);
const lastTrackedPointPercentage = ref<TrackingWatchedPointsInPercentage | -1>(-1);
const lastTrackedPointSeconds = ref<TrackingWatchedPointsInSeconds | -1>(-1);

function setVideoDuration() {
	if (!videoElement.value) return;
	videoDuration.value = videoElement.value.duration;
}

function onTimeUpdate() {
	const currentTime = videoElement.value?.currentTime ?? 0;

	/** Percentage Tracking */
	if (lastTrackedPointPercentage.value < 100 && videoDuration.value > 0) {
		const percentageViewed = Math.ceil((currentTime / videoDuration.value) * 100);
		const nextTrackingPointPercent = TrackingWatchedPointsInPercentage.find(
			point => point > lastTrackedPointPercentage.value
		);

		if (nextTrackingPointPercent != null && percentageViewed >= nextTrackingPointPercent) {
			lastTrackedPointPercentage.value = nextTrackingPointPercent;

			trackSraEvent({
				action: SraEventAction.VideoView,
				sponsoredAd: () => props.sponsoredAd,
				progress: nextTrackingPointPercent,
			});
		}
	}

	/** Seconds Tracking */
	if (lastTrackedPointSeconds.value < 15) {
		const secondsViewed = Math.floor(currentTime);
		const nextTrackingPointSeconds = TrackingWatchedPointsInSeconds.find(
			point => point > lastTrackedPointSeconds.value
		);

		if (nextTrackingPointSeconds != null && secondsViewed >= nextTrackingPointSeconds) {
			lastTrackedPointSeconds.value = nextTrackingPointSeconds;

			trackSraEvent({
				action: SraEventAction.VideoViewSeconds,
				sponsoredAd: () => props.sponsoredAd,
				progress: nextTrackingPointSeconds,
			});
		}
	}
}

/**
 * `this.onTimeUpdate` doesn't always fire at 100 before restarting the loop.
 * So we trigger it manually to guarantee it gets tracked.
 */
function onVideoEnded() {
	if (!videoElement.value) return;
	videoElement.value.play();
}

const fullscreenIcon = computed(() => {
	return `/${ASSETS_DIR}/img/sponsored-recommendation-assets/fullscreen.svg`;
});

/** This only handles entering fullscreen and not exiting, because there are
 * only native controls in fullscreen */
async function requestFullscreen() {
	try {
		if (!videoElement.value) return;

		if ('webkitSupportsFullscreen' in videoElement.value && videoElement.value.webkitSupportsFullscreen === true) {
			videoElement.value.webkitEnterFullscreen();
		}

		await videoElement.value.requestFullscreen();
	} catch (error) {
		// Nothing to do here
	}
}

function onChangeFullscreen() {
	const change = Boolean(document.fullscreenElement) ? 'entered' : 'exited';
	TrackingHelper.trackEvent(
		ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
		{
			action: 'toggle_fullscreen',
			value: change === 'entered' ? 1 : 0,
		},
		[titleContext.value]
	);
}

// Campaigns that should use the lighter gradient
const lighterGradientCampaigns = ['PRIMATE_AU_BURST1_SRA'];
/**  Boolean value to determine if we should use the lighter gradient for the video overlay */
const useLighterGradient = computed(() => lighterGradientCampaigns.includes(props.sponsoredAd.campaign?.name ?? ''));

return { __sfc: true,TrackingWatchedPointsInPercentage, TrackingWatchedPointsInSeconds, props, emits, campaignNode, promotionalTitle, promotionalText, trackSraEvent, disclaimerText, isVideoAsGifCampaign, getReleaseDate, posterSRImage, timerToMatchStarts, hasTimeRemaining, onVolumeChange, checkIfHasTimeRemaining, videoElement, isMuted, DefineVideoControls, ReuseVideoControls, volumeIcon, titleContext, toggleMute, videoDuration, lastTrackedPointPercentage, lastTrackedPointSeconds, setVideoDuration, onTimeUpdate, onVideoEnded, fullscreenIcon, requestFullscreen, onChangeFullscreen, lighterGradientCampaigns, useLighterGradient, CountdownTimer, PictureComp }
}

})